import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"

import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import WarningIcon from "@mui/icons-material/Warning"
import { CircularProgress, Divider } from "@mui/material"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Grid from "@mui/material/Grid"
import { Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { MRT_ColumnDef } from "material-react-table"
import moment from "moment"

import { InfoContainer } from "../../components/core/InfoContainer"
import { KonanPageHeader } from "../../components/core/KonanPageHeader"
import { BaseTable } from "../../components/tables/BaseTable"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { AdjustedDriftJob, AdjustedDriftSkewInfo, DriftResponse, ProjectLimits } from "../../types/custom/projects"
import type { TrainingDataUpload } from "../../types/generated/api/TrainingDataUpload"
import { mapSeverityToFeatures } from "../../utils/modelDetailsHelpers"

import styles from "./DataDrift.module.scss"

const SeverityContainer = (props: { driftData: AdjustedDriftSkewInfo[]; status: string }): React.ReactElement => {
  const { driftData, status } = props
  const { t } = useTranslation("ModelDrift")

  const filterBySeverity = (features: AdjustedDriftSkewInfo[]): AdjustedDriftSkewInfo[] => {
    return features.filter((feature: AdjustedDriftSkewInfo) => feature.error)
  }

  const severityData = filterBySeverity(driftData)

  return (
    <Fragment>
      {severityData.length === 1 ? (
        <Typography variant="span-regular">
          <strong>{severityData[0]?.featureName}</strong>{" "}
          <span className={"warning-text"}>{t("severity.singleRequiresAttention")}</span>
        </Typography>
      ) : severityData.length === 2 ? (
        <Typography variant="span-regular">
          <strong>{severityData[0]?.featureName} </strong> {t("and")} <strong>{severityData[1]?.featureName}</strong>{" "}
          <span className={"warning-text"}>{t("severity.multipleRequireAttention")}</span>
        </Typography>
      ) : severityData.length === 3 ? (
        <Typography variant="span-regular">
          <strong>{severityData[0]?.featureName}</strong>, <strong>{severityData[1]?.featureName}</strong> {t("and")}{" "}
          <strong>1</strong> {t("severity.oneMoreFeature")}{" "}
          <span className={"warning-text"}>{t("severity.multipleRequireAttention")}</span>
        </Typography>
      ) : severityData.length > 3 ? (
        <Typography variant="span-regular">
          <strong>{severityData[0]?.featureName}</strong>, <strong>{severityData[1]?.featureName}</strong> {t("and")}{" "}
          <strong>{(severityData.length - 2).toString()}</strong> {t("severity.moreFeatures")}{" "}
          <span className={"warning-text"}>{t("severity.multipleRequireAttention")}</span>
        </Typography>
      ) : status === "error" ? (
        <Typography variant="span-regular">
          <span className={"error-text"}>{t("messages.errorRunningCheck")}</span>
        </Typography>
      ) : (
        <Typography variant="span-regular">
          <span className={"good-text"}>{t("messages.noIssues")}</span>
        </Typography>
      )}
    </Fragment>
  )
}

export function DataDriftView(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()
  const { t } = useTranslation("ModelDrift")
  const columns: MRT_ColumnDef<AdjustedDriftSkewInfo>[] = React.useMemo(
    () => [
      {
        header: t("tables.columns.severity"),
        accessorFn: (row) => (row.error ? t("labels.warning") : t("labels.good")),
        Cell: ({ row }) => {
          if (row.original.error) {
            return (
              <Typography variant="h3-regular">
                <WarningIcon className={"warning-table-icon"} /> {t("labels.warning")}
              </Typography>
            )
          } else {
            return (
              <Typography variant="h3-regular">
                <CheckCircleIcon className={"good-table-icon"} /> {t("labels.good")}
              </Typography>
            )
          }
        },
      },
      {
        header: t("tables.columns.name"),
        accessorKey: "featureName",
      },
      {
        header: t("tables.columns.value"),
        accessorFn: (row) => {
          if (row.driftValue === null) return "-"
          const percentValue = row.driftValue * 100
          return (percentValue % 1 === 0 ? percentValue : percentValue.toFixed(1)) + "%"
        },
      },
      {
        header: t("tables.columns.threshold"),
        accessorFn: (row) => {
          if (row.driftThreshold === null) return "-"
          const percentValue = row.driftThreshold * 100
          return (percentValue % 1 === 0 ? percentValue : percentValue.toFixed(1)) + "%"
        },
      },
      {
        header: t("tables.columns.warningDescription"),
        accessorKey: "errorReason.shortDescription",
      },
    ],
    [t],
  )

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const navigate = useNavigate()
  const theme = getTheme()
  const pageSize = 5

  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = React.useState<string | false>("panel0")

  const {
    isFetching,
    isError,
    isLoading: isDriftDataLoading,
    data: driftResponse,
  } = useQuery<AxiosResponse<DriftResponse>, AxiosError>(
    ["driftJobs", currentModel?.uuid, page, pageSize],
    () => KonanAPI.fetchDataDriftJobs(currentModel?.uuid as string, page, pageSize),
    { keepPreviousData: true, enabled: !!currentModel?.uuid },
  )

  const { data: trainingData, isLoading: isTrainingDataLoading } = useQuery<TrainingDataUpload[], AxiosError>(
    ["training-data", projectId],
    () => KonanAPI.fetchTrainingData(projectId as string),
  )

  const { data: projectLimits } = useQuery<AxiosResponse<ProjectLimits>, AxiosError>(["project-limits"], () =>
    KonanAPI.fetchProjectLimits(),
  )

  const driftData = React.useMemo(() => {
    if (driftResponse?.data?.results) {
      return mapSeverityToFeatures(driftResponse?.data?.results)
    }
    return []
  }, [driftResponse?.data?.results])

  const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false)
  }

  return (
    <React.Fragment>
      <Grid container direction="row" spacing={2} marginBottom={1}>
        <KonanPageHeader title={t("title")} />
      </Grid>
      <Grid container spacing={2}>
        {/* Info Section */}
        {isTrainingDataLoading || isDriftDataLoading || (isFetching && !isError) ? (
          <InfoContainer icon={<CircularProgress className={styles.circularProgress} />} title={t("loading.title")} />
        ) : isError ? (
          <InfoContainer
            icon={<ErrorIcon style={{ color: theme.palette.red.text[2] }} fontSize="large" />}
            title={t("error.title")}
            subtitle={t("error.subtitle")}
          />
        ) : (
          driftData?.length === 0 &&
          // If there's no valid training data attached to the project
          (trainingData && trainingData.some((e) => e.is_valid) ? (
            <KonanEmptyState title={t("empty.noChecksTitle")} subTitle={t("empty.noChecksSubtitle")} />
          ) : (
            // If there's a valid training data file and no data drift jobs have been created yet
            <KonanEmptyState
              title={t("empty.noTrainingTitle")}
              subTitle={t("empty.noTrainingSubtitle")}
              buttonText={t("actions.uploadTrainingData")}
              setAction={() => navigate(`/projects/${projectId}/model-training?page=Training-Data`)}
            />
          ))
        )}

        {driftResponse && driftResponse?.data.results.length > 0 && !isFetching && (
          <Grid item xs={12}>
            {driftData.map((item: AdjustedDriftJob, index: number) => (
              <Accordion
                key={index}
                expanded={expanded === "panel" + index}
                onChange={handleChange("panel" + index)}
                className={styles.accordionBase}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon style={{ color: theme.palette.base.text[2] }} />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                    <Grid item xs={6}>
                      <Grid
                        container
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Grid item container spacing={1}>
                          {item.created_at && (
                            <Grid item>
                              <Typography variant="h3-regular">
                                {t("summary.prefix")}{" "}
                                <strong>{moment(item.created_at).format("DD/MM/YYYY, h:mm A")}</strong>{" "}
                                {t("summary.using")} <strong>{projectLimits?.data.drift_threshold}</strong>{" "}
                                {t("labels.requests")}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      <Grid item container>
                        <SeverityContainer driftData={item.result} status={item.status} />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      container
                      xs={6}
                      justifyContent="flex-end"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexWrap: "nowrap" }}
                    >
                      <Grid item sx={{ minWidth: 0 }}>
                        <Typography variant="h3-regular" noWrap>
                          <strong>{t("labels.trainingData")}</strong>: {item.reference_data}
                        </Typography>
                      </Grid>
                      <Divider orientation="vertical" flexItem className={styles.divider} />
                      <Grid item sx={{ whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>
                        {item.status === "error" ? (
                          <Typography variant="p">
                            <ErrorIcon className={styles.error} /> {t("labels.error")}
                          </Typography>
                        ) : item.result?.filter((feature: AdjustedDriftSkewInfo) => feature.error).length > 0 ? (
                          <Typography variant="p">
                            <WarningIcon className={"warning-icon"} /> {t("labels.warning")}
                          </Typography>
                        ) : (
                          <Typography variant="p">
                            <CheckCircleIcon className={styles.good} /> {t("labels.good")}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container direction="row" justifyContent="center" alignItems="center">
                    {item.status !== "error" ? (
                      <Grid item xs={12}>
                        <BaseTable
                          enableOrdering={false}
                          disableToolbarActions
                          columns={columns as MRT_ColumnDef[]}
                          data={item.result}
                          title={t("tables.title")}
                          isLoading={false}
                        />
                      </Grid>
                    ) : (
                      <Typography variant="h3-bold">{t("messages.noData")}</Typography>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        )}

        {/* Pagination Section */}
        {driftResponse && driftResponse?.data.results.length > 0 && !isFetching && (
          <Grid item>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalCount={driftResponse?.data?.count ? driftResponse.data.count : 0}
              onPageChange={(page: number) => {
                setPage(page)
                window.scrollTo(0, 0)
              }}
              variant={"complex"}
              hidePageSize={true}
            />
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  )
}
